import { useLazyQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { Autocomplete, useDebouncedQuery } from 'components/v2/Autocomplete'
import { GET_PLACES_SUGGESTIONS } from 'features/Places/queries-legacy'
import { IAutocompleteProps } from 'components/v2/Autocomplete/types'
import { GTPlaceSuggestion, GTTPlaceSuggestionsResponse } from 'gql/types.generated'

const messages = defineMessages({
  error: {
    id: 'places.autocomplete.error',
    defaultMessage: 'There was an issue while loading your places'
  },
  empty: {
    id: 'places.autocomplete.empty',
    defaultMessage: 'No places found'
  }
})

interface IPlaceAutocompleteProps extends Omit<IAutocompleteProps, 'onChange' | 'items' | 'getItemLabel'> {
  onChange: (place: GTPlaceSuggestion) => void
  businessSiteId?: string
  businessClientId?: string
  companyId?: string
  vertical?: string
  includeWithNoSites?: boolean
}

export const PlaceAutocomplete = ({
  onChange,
  companyId,
  businessClientId,
  businessSiteId,
  vertical,
  includeWithNoSites,
  ...props
}: IPlaceAutocompleteProps): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')

  const [getPlaces, { loading, error, data }] = useLazyQuery<{ tplaceSuggestions: GTTPlaceSuggestionsResponse }>(
    GET_PLACES_SUGGESTIONS
  )
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    query: getPlaces,
    pattern,
    extraInputs: {
      companyID: companyId || null,
      businessClientID: businessClientId || null,
      businessSiteID: businessSiteId || null,
      vertical: vertical || null,
      includeWithNoSites
    }
  })

  const handleChange = (place: GTPlaceSuggestion, { inputValue = '' } = {}): void => {
    setPattern(inputValue)
    if (!isEmpty(place)) {
      onChange(place)
    } else {
      onChange({
        id: '',
        name: '',
        location: {
          latitude: '',
          longitude: ''
        },
        stateCode: ''
      })
    }
  }

  const shownItems = data?.tplaceSuggestions.items || []

  return (
    <Autocomplete
      {...props}
      items={shownItems}
      getItemLabel={(place: GTPlaceSuggestion): string => `${place.name} (${place.stateCode})`}
      loading={loading || isDebouncing}
      error={error}
      errorMessage={intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      onChange={handleChange}
      shouldShowItems={shouldShowItems}
    />
  )
}
